import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  catchError,
  finalize,
  forkJoin,
  of,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Tooltip } from 'primeng/tooltip';
import { CadFile } from '../../../models/cad-file.model';
import { CadStorageFile } from '../../../models/storage.model';
import { StorageService } from '../../services/storage.service';
import { CadFileService } from '../../services/cad-file.service';
import { debouncedSearchTerms } from '../../../utils/list-search';

@Component({
  selector: 'app-use-existing-cad',
  imports: [
    DatePipe,
    ReactiveFormsModule,
    ButtonModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    Paginator,
    Tooltip,
  ],
  templateUrl: './use-existing-cad.component.html',
  styleUrl: './use-existing-cad.component.scss',
})
export class UseExistingCadComponent implements OnInit, OnDestroy {
  private cadFileService = inject(CadFileService);
  private storageService = inject(StorageService);

  @Input() multiple = false;
  @Input() forMoulding?: boolean;
  @Output() filesSelected = new EventEmitter<CadFile[]>();

  storageFiles: CadStorageFile[] = [];
  searchQuery = new FormControl('', { nonNullable: true });
  activeSearch = '';
  totalRecords = 0;
  rows = 20;
  first = 0;
  selectedCadFileIds = new Set<number>();
  loadingStorage = false;
  loadError: string | null = null;
  loadingSelectedExisting = false;
  storageError: string | null = null;

  private storageRequests = new Subject<void>();
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      this.storageRequests
        .pipe(
          switchMap(() => {
            this.loadingStorage = true;
            return this.storageService
              .list('cad', {
                limit: this.rows,
                offset: this.first,
                forMoulding: this.forMoulding,
                search: this.activeSearch,
              })
              .pipe(catchError(() => of(null)));
          }),
        )
        .subscribe((response) => {
          this.loadingStorage = false;
          this.storageFiles = response?.results ?? [];
          this.totalRecords = response?.count ?? 0;
          this.loadError = response ? null : 'Failed to load Storage files.';
        }),
    );

    this.subscriptions.add(
      debouncedSearchTerms(this.searchQuery.valueChanges).subscribe(
        (search) => {
          this.activeSearch = search;
          this.first = 0;
          this.loadStorageFiles();
        },
      ),
    );

    this.loadStorageFiles();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get useSelectedLabel(): string {
    if (!this.multiple) {
      return 'Use Selected CAD File';
    }
    const count = this.selectedCadFileIds.size;
    return count > 0
      ? `Use Selected CAD Files (${count})`
      : 'Use Selected CAD Files';
  }

  loadStorageFiles(): void {
    this.storageRequests.next();
  }

  refreshStorageFiles(): void {
    this.selectedCadFileIds.clear();
    this.loadStorageFiles();
  }

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? this.rows;
    this.loadStorageFiles();
  }

  isSelected(fileId: number): boolean {
    return this.selectedCadFileIds.has(fileId);
  }

  toggleExistingCadFileSelection(cadFile: CadStorageFile): void {
    const fileId = cadFile.id;

    if (this.multiple) {
      if (this.selectedCadFileIds.has(fileId)) {
        this.selectedCadFileIds.delete(fileId);
      } else {
        this.selectedCadFileIds.add(fileId);
      }
      return;
    }

    if (this.selectedCadFileIds.has(fileId)) {
      this.selectedCadFileIds.clear();
      return;
    }

    this.selectedCadFileIds.clear();
    this.selectedCadFileIds.add(fileId);
  }

  useSelectedExistingCadFiles(): void {
    const selectedIds = Array.from(this.selectedCadFileIds);

    if (selectedIds.length === 0) {
      return;
    }

    this.loadingSelectedExisting = true;
    this.storageError = null;

    const subscription = forkJoin(
      selectedIds.map((id) => this.cadFileService.getCadFile(id)),
    )
      .pipe(
        finalize(() => {
          this.loadingSelectedExisting = false;
        }),
      )
      .subscribe({
        next: (files) => {
          this.filesSelected.emit(files);
        },
        error: () => {
          this.storageError = 'Failed to load selected CAD files.';
        },
      });

    this.subscriptions.add(subscription);
  }
}
